const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';
const API_PREFIX = '/api/v1';

export const API_URL = `${API_BASE}${API_PREFIX}`;

export const API_ROUTES = {
  VERSION: '/version',
  DASHBOARD: {
    DIETITIAN: '/dashboard/dietitian',
  },
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/reset/request',
    RESET_PASSWORD: '/auth/reset',
    UPDATE_PASSWORD: '/auth/update-password',
    GOOGLE: '/auth/google',
    IMPERSONATE: '/auth/impersonate',
    SEND_SIGNUP_OTP: '/auth/signup/send-otp',
    VERIFY_SIGNUP_OTP: '/auth/signup/verify-otp',
    ONBOARDING_CONTEXT: '/auth/onboarding-context',
  },
  USERS: {
    ME: '/users/me',
    PROFILE: '/users/profile',
    LIST: '/users/list',
    NAMES: (teamOnly?: boolean) =>
      teamOnly ? '/users/names?teamOnly=true' : '/users/names',
    INVITE: '/users/invite',
    CREATE: '/users/create',
    ACCEPT_INVITE: '/users/accept-invite',
    RESEND_INVITE: '/users/resend-invite',
    COMPLETE_TOUR: '/users/tour-complete',
    UPDATE: (id: string) => `/users/${id}` as const,
    BLOCK: (id: string) => `/users/${id}/block` as const,
    UNBLOCK: (id: string) => `/users/${id}/unblock` as const,
    HIERARCHY: '/users/hierarchy',
    SET_MANAGER: (id: string) => `/users/${id}/set-manager` as const,
    DESCENDANTS: (id: string) => `/users/${id}/descendants` as const,
    RESET_PASSWORD: (id: string) => `/users/${id}/reset-password` as const,
    PAGE_PREFERENCES: '/users/me/page-preferences',
  },
  ROLES: {
    LIST: '/roles',
    LIST_FULL: '/roles/list',
    CREATE: '/roles',
    GET: (id: string) => `/roles/${id}` as const,
    UPDATE: (id: string) => `/roles/${id}` as const,
    DELETE: (id: string) => `/roles/${id}` as const,
  },
  AUDIT: {
    LIST: '/audit/list',
    GET: (id: string) => `/audit/${id}` as const,
  },
  SESSIONS: {
    LIST: '/sessions',
    REVOKE: (id: string) => `/sessions/${id}/revoke` as const,
  },
  ANNOUNCEMENTS: {
    CREATE: '/announcements',
    LIST: '/announcements/list',
    FEED: '/announcements/feed',
    MARK_READ: (id: string) => `/announcements/${id}/read` as const,
    UPDATE: (id: string) => `/announcements/${id}` as const,
    DELETE: (id: string) => `/announcements/${id}` as const,
  },
  NOTIFICATION_FEED: {
    FEED: '/me/notifications',
    MARK_READ: (id: string) => `/me/notifications/${id}/read` as const,
    READ_ALL: '/me/notifications/read-all',
    WEB_PUSH_PUBLIC_KEY: '/me/notifications/web-push/public-key',
    WEB_PUSH_SUBSCRIBE: '/me/notifications/web-push/subscribe',
    WEB_PUSH_UNSUBSCRIBE: '/me/notifications/web-push/unsubscribe',
    TEST: '/me/notifications/dev/test',
  },
  EXPORTS: {
    ENTITIES: '/exports/entities',
    FIELDS: (entityType: string) => `/exports/fields/${entityType}` as const,
    FILTERS: (entityType: string) => `/exports/filters/${entityType}` as const,
    CREATE: '/exports',
    LIST: '/exports/list',
    CANCEL: (id: string) => `/exports/${id}/cancel` as const,
    DOWNLOAD_URL: (id: string) => `/exports/${id}/download-url` as const,
  },
  RULES: {
    CREATE: '/rules',
    LIST: '/rules/list',
    GET: (id: string) => `/rules/${id}` as const,
    UPDATE: (id: string) => `/rules/${id}` as const,
    DELETE: (id: string) => `/rules/${id}` as const,
    FACTS: '/rules/facts',
    FACT_VALUES: (factId: string) => `/rules/facts/${factId}/values` as const,
    ACTIONS: '/rules/actions',
    RUNS_LIST: (id: string) => `/rules/${id}/runs/list` as const,
  },
  INTEGRATIONS: {
    LIST: '/integrations',
    CONNECT: (provider: string) => `/integrations/${provider}/connect` as const,
    DISCONNECT: (provider: string) => `/integrations/${provider}` as const,
    CREDENTIALS: (provider: string) =>
      `/integrations/${provider}/credentials` as const,
    HEALTH_CHECK: (provider: string) =>
      `/integrations/${provider}/health-check` as const,
    RAZORPAY_WEBHOOK_TOKEN: '/integrations/razorpay/webhook-token',
    RAZORPAY_WEBHOOK: (token: string) =>
      `/integrations/razorpay/webhook/${token}` as const,
  },
  WHATSAPP: {
    PLACEHOLDERS: '/whatsapp/placeholders',
    TEMPLATES: '/whatsapp/templates',
    TEMPLATES_SYNC: '/whatsapp/templates/sync',
    TEMPLATE: (id: string) => `/whatsapp/templates/${id}` as const,
    TEMPLATE_MAPPING: (id: string) =>
      `/whatsapp/templates/${id}/mapping` as const,
    PREVIEW: '/whatsapp/messages/preview',
    SEND: '/whatsapp/messages/send',
    MESSAGES_LIST: '/whatsapp/messages/list',
    MESSAGES_LOG: '/whatsapp/messages/log',
  },
  CALLS: {
    INITIATE: (contactId: string) => `/calls/${contactId}/initiate` as const,
    LIST_FOR_CONTACT: (contactId: string) =>
      `/calls/contact/${contactId}` as const,
  },
  LEADS: {
    LIST: '/leads/list',
    CREATE: '/leads',
    GET: (id: string) => `/leads/${id}` as const,
    UPDATE: (id: string) => `/leads/${id}` as const,
    UPDATE_STATUS: (id: string) => `/leads/${id}/status` as const,
    ASSIGN: (id: string) => `/leads/${id}/assign` as const,
    DELETE: (id: string) => `/leads/${id}` as const,
    NOTES: (id: string) => `/leads/${id}/notes` as const,
    INTERACTIONS: (id: string) => `/leads/${id}/interactions` as const,
  },
  LEAD_FORMS: {
    LIST: '/lead-forms/list',
    CREATE: '/lead-forms',
    GET: (id: string) => `/lead-forms/${id}` as const,
    UPDATE: (id: string) => `/lead-forms/${id}` as const,
    DELETE: (id: string) => `/lead-forms/${id}` as const,
    SUBMISSIONS: (formId: string) =>
      `/lead-forms/${formId}/submissions/list` as const,
    GET_SUBMISSION: (id: string) => `/lead-forms/submissions/${id}` as const,
    DELETE_SUBMISSION: (id: string) => `/lead-forms/submissions/${id}` as const,
    STATS: (formId: string) => `/lead-forms/${formId}/stats` as const,
  },
  MEAL_TIME_TEMPLATES: {
    LIST: '/meal-time-templates',
    CREATE: '/meal-time-templates',
    UPDATE: (id: string) => `/meal-time-templates/${id}` as const,
    DELETE: (id: string) => `/meal-time-templates/${id}` as const,
  },
  DIET_GUIDELINES: {
    LIST: '/diet-guidelines/list',
    CREATE: '/diet-guidelines',
    GET: (id: string) => `/diet-guidelines/${id}` as const,
    UPDATE: (id: string) => `/diet-guidelines/${id}` as const,
    DELETE: (id: string) => `/diet-guidelines/${id}` as const,
  },
  DIET_TEMPLATES: {
    LIST: '/diet-templates/list',
    CREATE: '/diet-templates',
    GET: (id: string) => `/diet-templates/${id}` as const,
    UPDATE: (id: string) => `/diet-templates/${id}` as const,
    DELETE: (id: string) => `/diet-templates/${id}` as const,
  },
  FOODS: {
    LIST: '/foods/list',
    SEARCH: '/foods/search',
    CREATE: '/foods',
    ESTIMATE_NUTRITION: '/foods/estimate-nutrition',
    DETECT_CUSTOM: '/foods/detect-custom',
    BULK_CREATE: '/foods/bulk-create',
    GET: (id: string) => `/foods/${id}` as const,
    UPDATE: (id: string) => `/foods/${id}` as const,
    DELETE: (id: string) => `/foods/${id}` as const,
  },
  EXERCISES: {
    LIST: '/exercises/list',
    SEARCH: '/exercises/search',
    CREATE: '/exercises',
    GET: (id: string) => `/exercises/${id}` as const,
    UPDATE: (id: string) => `/exercises/${id}` as const,
    DELETE: (id: string) => `/exercises/${id}` as const,
  },
  WORKOUT_TEMPLATES: {
    LIST: '/workout-templates/list',
    CREATE: '/workout-templates',
    GET: (id: string) => `/workout-templates/${id}` as const,
    UPDATE: (id: string) => `/workout-templates/${id}` as const,
    DELETE: (id: string) => `/workout-templates/${id}` as const,
    DUPLICATE: (id: string) => `/workout-templates/${id}/duplicate` as const,
  },
  CLIENT_WORKOUT: {
    LIST: '/client-workout/list',
    CREATE: '/client-workout',
    BULK_CREATE: '/client-workout/bulk',
    ASSIGN_TEMPLATE: '/client-workout/assign-template',
    PUBLISH: '/client-workout/publish',
    GET_BY_DATE: (contactId: string, date: string) =>
      `/client-workout/${contactId}/${date}` as const,
    DELETE: (contactId: string, date: string) =>
      `/client-workout/${contactId}/${date}` as const,
  },
  SEARCH: {
    KEY: '/search/key',
    KEY_FOR: (collection: string) =>
      `/search/key?collection=${collection}` as const,
    SYNC: '/search/sync',
  },
  CLIENTS: {
    LIST: '/clients/list',
    OPTIONS: '/clients/options',
    DIETITIAN_LOADS: '/clients/dietitian-loads',
    BULK_TRANSFER: '/clients/bulk-transfer',
    CREATE: '/clients',
    GET: (id: string) => `/clients/${id}` as const,
    UPDATE: (id: string) => `/clients/${id}` as const,
    ASSIGN: (id: string) => `/clients/${id}/assign` as const,
    CARE_TEAM: (id: string) => `/clients/${id}/care-team` as const,
    ARCHIVE: (id: string) => `/clients/${id}/archive` as const,
    REGENERATE_PASSWORD: (id: string) =>
      `/clients/${id}/regenerate-password` as const,
    CONVERT_FROM_LEAD: '/clients/convert-from-lead',
    DELETED_ACCOUNTS: '/clients/deleted-accounts/list',
    RESTORE_ACCOUNT: (id: string) => `/clients/${id}/restore-account` as const,
    PERIODS_LIST: (id: string) => `/clients/${id}/periods/list` as const,
    PERIODS_CYCLE_SUMMARY: (id: string) =>
      `/clients/${id}/periods/cycle-summary` as const,
    INSIGHTS: (id: string) => `/clients/${id}/insights` as const,
    DEVICES: (id: string) => `/clients/${id}/devices` as const,
  },
  COVERAGE: {
    LIST: '/coverage',
    CREATE: '/coverage',
    MINE: '/coverage/mine',
    REVOKE: (id: string) => `/coverage/${id}` as const,
  },
  NOTIFICATIONS: {
    TEMPLATES: '/notifications/templates',
    SEND_TO_CLIENT: (id: string) => `/notifications/clients/${id}` as const,
    BROADCAST: '/notifications/broadcast',
    LIST: '/notifications/list',
  },
  SUBSCRIPTIONS: {
    LIST: '/subscriptions/list',
    CREATE: '/subscriptions',
    GET: (id: string) => `/subscriptions/${id}` as const,
    UPDATE: (id: string) => `/subscriptions/${id}` as const,
    DELETE: (id: string) => `/subscriptions/${id}` as const,
    RECORD_PAYMENT: (id: string) => `/subscriptions/${id}/payments` as const,
    LIST_PAYMENTS: (id: string) => `/subscriptions/${id}/payments` as const,
  },
  PAYMENTS: {
    UPDATE: (id: string) => `/payments/${id}` as const,
    DELETE: (id: string) => `/payments/${id}` as const,
  },
  CONTACT_ACTIVITIES: {
    LIST: '/contact-activities/list',
    CREATE: '/contact-activities',
  },
  VITALS: {
    RECORD: '/vitals',
    RECORD_BULK: '/vitals/bulk',
    LIST: '/vitals/list',
    CHART: '/vitals/chart',
    LATEST: (contactId: string) => `/vitals/latest/${contactId}` as const,
    GET: (id: string) => `/vitals/${id}` as const,
    UPDATE: (id: string) => `/vitals/${id}` as const,
    DELETE: (id: string) => `/vitals/${id}` as const,
  },
  CLIENT_DIET: {
    CREATE: '/client-diet',
    BULK_CREATE: '/client-diet/bulk',
    BULK_DELETE: '/client-diet/bulk-delete',
    LIST: '/client-diet/list',
    EXPORT_PDF: '/client-diet/export-pdf',
    MEAL_TIME: {
      GET: (contactId: string) =>
        `/client-diet/meal-time/${contactId}` as const,
      UPDATE: (contactId: string) =>
        `/client-diet/meal-time/${contactId}` as const,
    },
    GET_BY_DATE: (contactId: string, date: string) =>
      `/client-diet/${contactId}/${date}` as const,
    DELETE: (contactId: string, date: string) =>
      `/client-diet/${contactId}/${date}` as const,
    CREATE_SHARE_LINK: '/client-diet/share-link',
    LIST_SHARE_LINKS: (contactId: string, includeExpired?: boolean) =>
      `/client-diet/share-links/${contactId}${includeExpired ? '?includeExpired=true' : ''}` as const,
    DELETE_SHARE_LINK: (id: string) => `/client-diet/share-link/${id}` as const,
  },
  CLIENT_RECIPES: {
    LIST: (contactId: string) => `/client-recipes/${contactId}` as const,
    SAVE: (contactId: string, foodItemId: string) =>
      `/client-recipes/${contactId}/${foodItemId}` as const,
    DELETE: (contactId: string, foodItemId: string) =>
      `/client-recipes/${contactId}/${foodItemId}` as const,
    ADD_TO_LIBRARY: (contactId: string, foodItemId: string) =>
      `/client-recipes/${contactId}/${foodItemId}/library` as const,
  },
  PLAN_PERIODS: {
    CREATE: '/plan-periods',
    LIST: '/plan-periods/list',
    GET: (id: string) => `/plan-periods/${id}` as const,
    UPDATE: (id: string) => `/plan-periods/${id}` as const,
    DELETE: (id: string) => `/plan-periods/${id}` as const,
  },
  NOTES: {
    CREATE: '/notes',
    LIST: '/notes/list',
    UPDATE: (id: string) => `/notes/${id}` as const,
    DELETE: (id: string) => `/notes/${id}` as const,
  },
  COUNSELLING: {
    LIST: '/counselling/list',
    CREATE: '/counselling',
    GET: (id: string) => `/counselling/${id}` as const,
    CONDUCT: (id: string) => `/counselling/${id}/conduct` as const,
    UPDATE_STATUS: (id: string) => `/counselling/${id}/status` as const,
    CLIENT: (clientId: string) => `/counselling/client/${clientId}` as const,
  },
  FOLLOW_UPS: {
    TRACK_TYPES: '/follow-ups/track-types',
    TRACK_TYPE: (id: string) => `/follow-ups/track-types/${id}` as const,
    CLIENT_COACHES: (clientId: string) =>
      `/follow-ups/clients/${clientId}/coaches` as const,
    COACH: (id: string) => `/follow-ups/coaches/${id}` as const,
    SERIES: '/follow-ups/series',
    CLIENT_SERIES: (clientId: string) =>
      `/follow-ups/clients/${clientId}/series` as const,
    SERIES_ITEM: (id: string) => `/follow-ups/series/${id}` as const,
    SERIES_PAUSE: (id: string) => `/follow-ups/series/${id}/pause` as const,
    SERIES_END: (id: string) => `/follow-ups/series/${id}/end` as const,
    CLIENT_LIST: (clientId: string) =>
      `/follow-ups/clients/${clientId}` as const,
    CALENDAR: '/follow-ups/calendar',
    GET: (id: string) => `/follow-ups/${id}` as const,
    UPDATE: (id: string) => `/follow-ups/${id}` as const,
    RESCHEDULE: (id: string) => `/follow-ups/${id}/reschedule` as const,
  },
  BOOKING: {
    PAGE: '/booking/page',
    TIME_OFF_LIST: '/booking/time-off/list',
    TIME_OFF: '/booking/time-off',
    TIME_OFF_ITEM: (id: string) => `/booking/time-off/${id}` as const,
    CONFLICTS: '/booking/conflicts',
  },
  RECIPES: {
    LIST: '/recipes/list',
    CREATE: '/recipes',
    GET: (id: string) => `/recipes/${id}` as const,
    UPDATE: (id: string) => `/recipes/${id}` as const,
    DELETE: (id: string) => `/recipes/${id}` as const,
    PUBLISH: (id: string) => `/recipes/${id}/publish` as const,
    UNPUBLISH: (id: string) => `/recipes/${id}/unpublish` as const,
    FORK: (id: string) => `/recipes/${id}/fork` as const,
    BY_FOOD_ITEM: (foodItemId: string) =>
      `/recipes/by-food/${foodItemId}` as const,
  },
  ORGANIZATION: {
    GET: '/organization',
    UPDATE: '/organization',
    SEED_DEMO_DATA: '/organization/seed-demo-data',
  },
  STORAGE: {
    UPLOAD_URL: '/storage/upload-url',
  },
  ATTACHMENTS: {
    LIST: '/attachments/list',
    BULK_CREATE: '/attachments/bulk',
    DOWNLOAD_URL: (id: string) => `/attachments/${id}/download-url` as const,
    DELETE: (id: string) => `/attachments/${id}` as const,
  },
  SYSTEM_ADMIN: {
    ORGANIZATIONS: {
      LIST: '/system-admin/organizations/list',
      CREATE: '/system-admin/organizations',
      UPDATE_STATUS: (id: string) =>
        `/system-admin/organizations/${id}/status` as const,
      ACCESS: (id: string) =>
        `/system-admin/organizations/${id}/access` as const,
      ADD_SUBSCRIPTION: (id: string) =>
        `/system-admin/organizations/${id}/subscription` as const,
      GET_DETAILS: (id: string) => `/system-admin/organizations/${id}` as const,
      SET_BILLING: (id: string) =>
        `/system-admin/organizations/${id}/billing` as const,
      SET_CONTROLS: (id: string) =>
        `/system-admin/organizations/${id}/controls` as const,
      GET_CLIENT_APP_CONFIG: (id: string) =>
        `/system-admin/organizations/${id}/client-app-config` as const,
      SET_CLIENT_APP_CONFIG: (id: string) =>
        `/system-admin/organizations/${id}/client-app-config` as const,
      MARK_PAYMENT: (id: string) =>
        `/system-admin/organizations/${id}/mark-payment` as const,
      CREATE_PAYMENT_LINK: (id: string) =>
        `/system-admin/organizations/${id}/payment-link` as const,
      LIST_PAYMENT_LINKS: (id: string) =>
        `/system-admin/organizations/${id}/payment-links/list` as const,
      DELETE_CLIENT: (orgId: string, clientId: string) =>
        `/system-admin/organizations/${orgId}/clients/${clientId}` as const,
      SEED_GUIDELINES: (id: string) =>
        `/system-admin/organizations/${id}/seed-guidelines` as const,
      SEED_DEMO_DATA: (id: string) =>
        `/system-admin/organizations/${id}/seed-demo-data` as const,
      SEED_REVIEW_CLIENT: (id: string) =>
        `/system-admin/organizations/${id}/seed-review-client` as const,
      REMOVE_DEMO_DATA: (id: string) =>
        `/system-admin/organizations/${id}/demo-data` as const,
      WIPE_DATA: (id: string) =>
        `/system-admin/organizations/${id}/wipe-data` as const,
    },
    RESET_LOGIN_LOCKOUT: '/system-admin/reset-login-lockout',
    SYNC_PERMISSIONS: '/system-admin/sync-permissions',
    BACKUPS: {
      LIST: '/system-admin/backups/list',
    },
    BANNERS: {
      GET: '/system-admin/banners',
      UPSERT: '/system-admin/banners',
    },
    SEARCH: {
      STATUS: '/system-admin/search/status',
      SYNC_ALL: '/system-admin/search/sync-all',
    },
    FOODS: {
      STATS_BY_ORG: '/system-admin/foods/stats-by-org',
      LIST: '/system-admin/foods/list',
      UPDATE: (id: string) => `/system-admin/foods/${id}` as const,
    },
    CRON: {
      STATUS: '/system-admin/cron/status',
      EXECUTIONS_LIST: '/system-admin/cron/executions/list',
      RUN: '/system-admin/cron/run',
    },
  },
  ACCOUNT_DELETION: {
    SUBMIT: '/account-deletion-requests',
    LIST: '/account-deletion-requests/list',
    UPDATE: (id: string) => `/account-deletion-requests/${id}` as const,
  },
  BANNERS: {
    ACTIVE: '/banners/active',
    DISMISS: '/banners/dismiss',
  },
  CONTENTS: {
    LIST: '/contents/list',
    CREATE: '/contents',
    GET: (id: string) => `/contents/${id}` as const,
    UPDATE: (id: string) => `/contents/${id}` as const,
    DELETE: (id: string) => `/contents/${id}` as const,
    PUBLISH: (id: string) => `/contents/${id}/publish` as const,
  },
  INVOICES: {
    LIST: '/invoices/list',
    CREATE: '/invoices',
    GET: (id: string) => `/invoices/${id}` as const,
    UPDATE: (id: string) => `/invoices/${id}` as const,
    UPDATE_STATUS: (id: string) => `/invoices/${id}/status` as const,
    GENERATE_PDF: (id: string) => `/invoices/${id}/generate-pdf` as const,
    SEND: (id: string) => `/invoices/${id}/send` as const,
    DELETE: (id: string) => `/invoices/${id}` as const,
  },
  IMPORT_HISTORY: {
    LOG: '/import-history',
    LIST: '/import-history/list',
  },
  BILLING: {
    PLANS: '/billing/plans',
    CHECKOUT: '/billing/checkout',
    INFO: '/billing/info',
    CANCEL: '/billing/cancel',
    SYNC: '/billing/sync',
    PAYMENT_LINKS: '/billing/payment-links/list',
  },
  ORGANIZATION_CONFIG: {
    CREATE: '/organization-config',
  },
  FORM_TEMPLATES: {
    GET: (moduleType: string) => `/form-templates/${moduleType}` as const,
    UPSERT: (moduleType: string) => `/form-templates/${moduleType}` as const,
  },
  ASSESSMENT_FORMS: {
    LIST: '/assessment-forms',
    CREATE: '/assessment-forms',
    GET: (id: string) => `/assessment-forms/${id}` as const,
    GET_FOR_CLIENT: (id: string, clientId: string) =>
      `/assessment-forms/${id}?clientId=${clientId}` as const,
    UPDATE: (id: string) => `/assessment-forms/${id}` as const,
    PUBLIC_LINK: (id: string) => `/assessment-forms/${id}/public-link` as const,
    RESPONSES: (id: string) =>
      `/assessment-forms/${id}/responses/list` as const,
    CREATE_CLIENT_LINK: '/assessment-forms/links',
    CLIENT_LINKS: (clientId: string) =>
      `/assessment-forms/links/client/${clientId}` as const,
    REVOKE_LINK: (linkId: string) =>
      `/assessment-forms/links/${linkId}` as const,
    SUBMIT_RESPONSE: '/assessment-forms/responses',
    CLIENT_RESPONSES: (clientId: string) =>
      `/assessment-forms/responses/client/${clientId}` as const,
    RESPONSE: (responseId: string) =>
      `/assessment-forms/responses/${responseId}` as const,
    RESOLVE_PENDING: (responseId: string) =>
      `/assessment-forms/responses/${responseId}/resolve` as const,
    PUBLIC_GET: (token: string) => `/public/assessment-forms/${token}` as const,
    PUBLIC_SUBMIT: (token: string) =>
      `/public/assessment-forms/${token}/submit` as const,
  },
  SYSTEM_ARTICLES: {
    CREATE: '/system-admin/articles',
    LIST: '/system-admin/articles/list',
    GET: (id: string) => `/system-admin/articles/${id}` as const,
    UPDATE: (id: string) => `/system-admin/articles/${id}` as const,
    PUBLISH: (id: string) => `/system-admin/articles/${id}/publish` as const,
    DELETE: (id: string) => `/system-admin/articles/${id}` as const,
    GENERATE_BRIEF: '/system-admin/articles/briefs/generate',
    GENERATE_ARTICLE: '/system-admin/articles/generate',
  },
  CHAT: {
    RT_TOKEN: '/chat/rt-token',
    CONVERSATIONS: '/chat/conversations',
    CONVERSATION_STATE: '/chat/conversations/state',
    START: '/chat/conversations/start',
    MESSAGES: '/chat/messages/send',
    MESSAGE_EDIT: '/chat/messages/edit',
    MESSAGE_DELETE: '/chat/messages/delete',
    MESSAGE_REACT: '/chat/messages/react',
    MESSAGE_PIN: '/chat/messages/pin',
    MESSAGES_PINNED: '/chat/messages/pinned',
    MESSAGES_LIST: '/chat/messages/list',
    READ: '/chat/conversations/read',
    TYPING: '/chat/conversations/typing',
    CLIENT_CONTEXT: '/chat/conversations/client-context',
    GROUPS: '/chat/groups',
    GROUP_MEMBERS: '/chat/groups/members',
    GROUP_DETAILS: '/chat/groups/details',
    GROUP_RENAME: '/chat/groups/rename',
    GROUP_LEAVE: '/chat/groups/leave',
    GROUP_ARCHIVE: '/chat/groups/archive',
    GROUP_MEMBERS_ADD: '/chat/groups/members/add',
    BROADCASTS: '/chat/broadcasts',
    BROADCASTS_LIST: '/chat/broadcasts/list',
    BROADCAST_DETAILS: '/chat/broadcasts/details',
  },
} as const;

export function buildApiUrl(route: string): string {
  return `${API_URL}${route}`;
}
